import { NgTemplateOutlet } from "@angular/common";
import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    TemplateRef,
    ViewEncapsulation,
    inject,
    signal,
} from "@angular/core";
import { TranslatePipe, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope as rootScope } from "@app/shared/i18n/scope";
import { scope } from "@app/shared/organisms/cerrix-linked-item-table/i18n/scope";
import { defer, first, Observable, Subscription } from "rxjs";
import { CerrixButtonComponent } from "../../atoms/cerrix-button/cerrix-button.component";
import { CerrixPaginationComponent } from "../../molecules/cerrix-pagination/cerrix-pagination.component";
import { CerrixConfirmDialogComponent } from "../cerrix-confirm-dialog/cerrix-confirm-dialog.component";
import { ConfirmationDialogSettings } from "../cerrix-confirm-dialog/confirmation-dialog-settings.model";
import { CerrixDialogService } from "../cerrix-dialog/cerrix-dialog-service.component";
import { LinkedItemTableRowActionsComponent } from "./shared/linked-item-table-row-actions.component";
import {
    ILinkedItem,
    LinkedItemTableCellContext,
    LinkedItemTableTemplates,
} from "./linked-item-table-template.models";

type DetailState<D> = { status: "closed" | "loading" | "error" } | { status: "loaded"; data: D };

let nextTableId = 0;

@Component({
    selector: "app-cerrix-linked-item-table",
    imports: [
        NgTemplateOutlet,
        TranslatePipe,
        CerrixButtonComponent,
        CerrixConfirmDialogComponent,
        CerrixPaginationComponent,
        LinkedItemTableRowActionsComponent,
    ],
    viewProviders: [provideTranslationScope(scope)],
    templateUrl: "./cerrix-linked-item-table.component.html",
    styleUrl: "./cerrix-linked-item-table.component.scss",
    encapsulation: ViewEncapsulation.None,
})
export class CerrixLinkedItemTableComponent<T extends ILinkedItem, D>
    implements OnChanges, OnDestroy
{
    @Input({ required: true }) title: string;
    @Input({ required: true }) items: readonly T[] = [];
    @Input({ required: true }) templates: LinkedItemTableTemplates<T, D>;
    @Input() disabled = false;
    /** Hides the link and unlink buttons, so the rows stay usable. */
    @Input() canLink = true;
    @Input() rowActions?: TemplateRef<LinkedItemTableCellContext<T>>;
    @Input() loadDetails?: (item: T) => Observable<NoInfer<D>>;
    @Input() unlinkConfirmation?: ConfirmationDialogSettings;
    @Output() linkItems = new EventEmitter<void>();
    @Output() openItem = new EventEmitter<T>();
    @Output() unlinkItem = new EventEmitter<T>();

    readonly rootScope = rootScope;
    readonly tableId = `cerrix-linked-item-table-${nextTableId++}`;
    readonly unlinkConfirmationDialogName = `${this.tableId}-unlink-confirmation`;
    readonly pageSizes = [5, 10, 25];
    readonly currentPage = signal(1);
    readonly pageSize = signal(5);
    readonly expandedItem = signal<T | null>(null);
    readonly detailState = signal<DetailState<D>>({ status: "closed" });

    private detailRequest?: Subscription;
    private itemToUnlink: T | null = null;
    private readonly dialogService = inject(CerrixDialogService);

    get visibleItems(): readonly T[] {
        const start = (this.currentPage() - 1) * this.pageSize();
        return this.items.slice(start, start + this.pageSize());
    }

    get showFooter(): boolean {
        return this.items.length > Math.min(...this.pageSizes);
    }

    get firstItem(): number {
        return (this.currentPage() - 1) * this.pageSize() + 1;
    }

    get lastItem(): number {
        return Math.min(this.currentPage() * this.pageSize(), this.items.length);
    }

    get expandable(): boolean {
        return !!this.templates.detailRow && !!this.loadDetails && !this.disabled;
    }

    get columnCount(): number {
        return this.templates.columns.length + 1;
    }

    requestUnlink(item: T): void {
        if (!this.unlinkConfirmation) {
            this.unlinkItem.emit(item);
            return;
        }
        this.itemToUnlink = item;
        this.dialogService.openDialog(this.unlinkConfirmationDialogName);
    }

    confirmUnlink(): void {
        const item = this.itemToUnlink;
        this.closeUnlinkConfirmation();
        if (item !== null) {
            this.unlinkItem.emit(item);
        }
    }

    closeUnlinkConfirmation(): void {
        this.itemToUnlink = null;
        this.dialogService.closeDialog(this.unlinkConfirmationDialogName);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!this.templates.columns.length) {
            throw new Error("CerrixLinkedItemTable requires at least one column.");
        }
        if (this.loadDetails && !this.templates.detailRow) {
            throw new Error(
                "CerrixLinkedItemTable requires a detailRow template when loadDetails is supplied.",
            );
        }
        if (changes.items) {
            this.currentPage.set(1);
        }
        if (changes.items || changes.disabled || changes.templates || changes.loadDetails) {
            this.closeDetails();
        }
    }

    ngOnDestroy(): void {
        this.detailRequest?.unsubscribe();
    }

    changePage(page: number): void {
        const lastPage = Math.ceil(this.items.length / this.pageSize());
        if (page < 1 || page > lastPage || page === this.currentPage()) {
            return;
        }
        this.closeDetails();
        this.currentPage.set(page);
    }

    changePageSize(value: string): void {
        const size = Number(value);
        if (!this.pageSizes.includes(size)) {
            throw new Error(`Unsupported linked-item-table page size: ${value}`);
        }
        this.closeDetails();
        this.pageSize.set(size);
        this.currentPage.set(1);
    }

    onRowInteraction(item: T, event: Event): void {
        const target = event.target;
        if (
            target instanceof Element &&
            target.closest(
                "a, button, input, select, textarea, label, summary, [role=button], [role=link], " +
                    "[contenteditable]:not([contenteditable=false]), [data-linked-item-table-action]",
            )
        ) {
            return;
        }
        if (!this.expandable) {
            return;
        }
        event.preventDefault();
        this.toggleDetails(item);
    }

    toggleDetails(item: T): void {
        if (!this.expandable) {
            return;
        }
        if (this.expandedItem() === item) {
            this.closeDetails();
        } else {
            this.closeDetails();
            this.expandedItem.set(item);
            this.fetchDetails(item);
        }
    }

    retryDetails(): void {
        const item = this.expandedItem();
        if (item !== null && !this.disabled && this.detailState().status === "error") {
            this.fetchDetails(item);
        }
    }

    private fetchDetails(item: T): void {
        this.detailRequest?.unsubscribe();
        this.detailState.set({ status: "loading" });
        const request = new Subscription();
        this.detailRequest = request;
        // defer routes synchronous loader failures through the same visible error/retry state.
        request.add(
            defer(() => this.loadDetails(item))
                .pipe(first())
                .subscribe({
                    next: (data) => {
                        if (this.detailRequest === request && !request.closed) {
                            this.detailState.set({ status: "loaded", data });
                        }
                    },
                    error: () => {
                        if (this.detailRequest === request && !request.closed) {
                            this.detailState.set({ status: "error" });
                        }
                    },
                }),
        );
    }

    private closeDetails(): void {
        this.detailRequest?.unsubscribe();
        this.detailRequest = undefined;
        this.expandedItem.set(null);
        this.detailState.set({ status: "closed" });
    }
}
